import React from 'react';
import { format } from 'date-fns';
import { Button, StatusBadge } from '../common';
import { Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { ClubManagementIconWell } from '../clubs/dashboard/ClubSectionIcons';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';
import {
  getTournamentStatusLabel,
  getTournamentStatusVariant,
} from '../../utils/tournamentStatusDisplay';

const countRegisteredPlayers = (tournament) => {
  const entries = tournament?.tournament_entries;
  if (!Array.isArray(entries)) return null;
  return entries.reduce((sum, entry) => {
    const nested = entry?.tournament_entry_players;
    if (Array.isArray(nested) && nested.length > 0) return sum + nested.length;
    return sum + 1;
  }, 0);
};

/**
 * Tournament card — name, stage, date, and player count.
 */
export function TournamentCard({
  tournament,
  className = '',
  canManage = false,
  onView,
  onDelete,
}) {
  const statusLabel = getTournamentStatusLabel(tournament.status);
  const dateLabel = (() => {
    if (tournament.tournament_date) {
      const [year, month, day] = tournament.tournament_date.split('-').map(Number);
      if (year && month && day) {
        const datePart = format(new Date(year, month - 1, day), 'EEE, d MMM yyyy');
        const timePart = tournament.start_time ? String(tournament.start_time).slice(0, 5) : null;
        return timePart ? `${datePart} · ${timePart}` : datePart;
      }
    }
    return tournament.created_at
      ? format(new Date(tournament.created_at), 'EEE, d MMM yyyy')
      : null;
  })();
  const playerCount = countRegisteredPlayers(tournament);
  const playerLabel =
    playerCount == null
      ? null
      : `${playerCount} player${playerCount === 1 ? '' : 's'}`;
  const champions =
    tournament.status === 'completed' && tournament.winning_team
      ? formatTournamentEntryLabel(tournament.winning_team)
      : null;

  return (
    <Card
      variant="default"
      size="small"
      className={`flex h-full flex-col gap-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <ClubManagementIconWell type="tournaments" />
        <Typography
          as="h3"
          className="min-w-0 flex-1 font-semibold leading-snug text-text-primary line-clamp-2"
        >
          {tournament.name || 'Tournament'}
        </Typography>
        <StatusBadge
          variant={getTournamentStatusVariant(tournament.status)}
          size="small"
          className={`shrink-0 ${
            tournament.status === 'draft'
              ? '!bg-surface-subtle !text-text-secondary ring-1 ring-inset ring-border-medium'
              : ''
          }`}
        >
          {statusLabel}
        </StatusBadge>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        {dateLabel ? (
          <div>
            <Typography as="dt" variant="caption" className="block text-text-muted">
              Date
            </Typography>
            <Typography as="dd" variant="caption" className="mt-0.5 block font-medium text-text-primary">
              {dateLabel}
            </Typography>
          </div>
        ) : null}
        {playerLabel ? (
          <div>
            <Typography as="dt" variant="caption" className="block text-text-muted">
              Players
            </Typography>
            <Typography as="dd" variant="caption" className="mt-0.5 block font-medium text-text-primary">
              {playerLabel}
            </Typography>
          </div>
        ) : null}
      </dl>

      {champions ? (
        <Typography variant="caption" className="block truncate font-semibold text-success">
          Champions: {champions}
        </Typography>
      ) : null}

      <div className="mt-auto flex items-center gap-2 pt-1">
        <Button
          variant="outline"
          size="small"
          className="min-w-0 flex-1"
          onClick={() => onView(tournament)}
        >
          View tournament
        </Button>
        {canManage && onDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tournament);
            }}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border-medium text-text-muted transition-colors hover:border-danger/40 hover:bg-danger-light/20 hover:text-danger focus:outline-none focus:ring-2 focus:ring-primary/30 sm:h-8 sm:w-8"
            title="Delete tournament"
            aria-label={`Delete ${tournament.name || 'tournament'}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </Card>
  );
}

export default TournamentCard;
